"use client";

import { Check, MoreHorizontal, RefreshCw, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { t, getDirectionFromText } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import type { AvatarLook } from "../types";
import { lookSupportsV } from "../types";
import EngineQualityBadge from "./engineQualityBadge";

interface AvatarLookCardProps {
    look: AvatarLook;
    isSelected?: boolean;
    onSelect?: (look: AvatarLook) => void;
    onDelete?: (look: AvatarLook) => void;
    onRefresh?: (look: AvatarLook) => void;
    className?: string;
}

export default function AvatarLookCard({
    look,
    isSelected,
    onSelect,
    onDelete,
    onRefresh,
    className,
}: AvatarLookCardProps) {
    const { locale, dir } = useLocale();
    const isReady = look.status === "completed";
    const isApplied = isReady && !!isSelected;
    // Clicking the already-applied look is a no-op (no point re-applying);
    // unready looks can't render so they shouldn't be selectable either.
    const isInteractive = isReady && !isApplied;

    const handleActivate = () => {
        if (!isInteractive) return;
        onSelect?.(look);
    };

    return (
        <div
            role="button"
            tabIndex={isInteractive ? 0 : -1}
            aria-disabled={!isReady}
            aria-pressed={isApplied}
            title={
                !isReady
                    ? t("avatars.look.unavailable", locale)
                    : isApplied
                      ? t("avatars.look.applied", locale)
                      : undefined
            }
            onClick={handleActivate}
            onKeyDown={(e) => {
                if (!isInteractive) return;
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleActivate();
                }
            }}
            className={cn(
                "group relative w-full rounded-lg border overflow-hidden",
                isInteractive &&
                    "cursor-pointer hover:ring-2 hover:ring-ring/50",
                isApplied && "ring-2 ring-primary cursor-default",
                !isReady && "opacity-60 cursor-not-allowed",
                className
            )}
        >
            <div className="aspect-square bg-muted relative">
                {look.previewImageUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        src={look.previewImageUrl}
                        alt={look.name}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
                        {t("avatars.look.no-preview", locale)}
                    </div>
                )}
                {!isReady && (
                    <span className="absolute top-1 start-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none bg-black/60 text-white backdrop-blur-sm ring-1 ring-white/15 shadow-sm">
                        {look.status === "processing"
                            ? t("avatars.look.training", locale)
                            : look.status}
                    </span>
                )}
                {isApplied && (
                    <span className="absolute top-1 start-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none bg-primary text-primary-foreground backdrop-blur-sm shadow-sm">
                        <Check className="size-2.5" />
                        {t("avatars.look.applied", locale)}
                    </span>
                )}
                {isReady && !lookSupportsV(look) && <EngineQualityBadge />}
                {(onDelete || onRefresh) && (
                    <div className="absolute top-1 end-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                        <DropdownMenu dir={dir}>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    className="size-6 rounded-full bg-black/60 text-white hover:bg-black/75 hover:text-white backdrop-blur-sm ring-1 ring-white/15 shadow-sm"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <MoreHorizontal className="size-3.5" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                className="min-w-0"
                            >
                                {onRefresh && (
                                    <DropdownMenuItem
                                        className="text-xs py-1"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRefresh(look);
                                        }}
                                    >
                                        <RefreshCw className="!size-3" />
                                        {t(
                                            "avatars.card.refresh-status",
                                            locale
                                        )}
                                    </DropdownMenuItem>
                                )}
                                {onDelete && (
                                    <DropdownMenuItem
                                        className="text-xs py-1 text-destructive"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDelete(look);
                                        }}
                                    >
                                        <Trash2 className="!size-3" />
                                        {t("avatars.card.delete", locale)}
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                )}
            </div>
            <div
                dir={getDirectionFromText(look.name)}
                className="p-1.5 w-full text-[11px] truncate text-start"
            >
                {look.name}
            </div>
        </div>
    );
}
